import { EditableLinksView } from "@softmax/product-ux/editor";
import type { StoreLink } from "../store-page/types";
import { useEditModeMaybe } from "../use-edit-mode-maybe";
import {
  linkLabelMaxCharacters,
  maxStoreLinks,
} from "../store-page/content-limits";
import { isAllowedLinkUrl } from "../store-page/link-validation";

const urlError = "https:// only — no credentials or IP-literal host";

export function EditableLinks({
  creatorLinks,
  hasLockedLinks,
}: {
  creatorLinks: readonly StoreLink[];
  hasLockedLinks: boolean;
}) {
  const mode = useEditModeMaybe();

  if (!mode?.editing)
    return (
      <EditableLinksView
        mode="read"
        links={creatorLinks.map((link) => ({
          ...link,
          kind: link.kind ?? null,
        }))}
        hasLockedLinks={hasLockedLinks}
      />
    );

  const { editor } = mode;
  const links = editor.content.links;
  return (
    <EditableLinksView
      mode="edit"
      hasLockedLinks={hasLockedLinks}
      links={links.map((link, index) => ({
        ...link,
        kind: link.kind ?? null,
        warnings: editor.lintWarnings.filter(
          (warning) => warning.field === `links[${index}].label`,
        ),
        urlError: isAllowedLinkUrl(link.url) ? null : urlError,
      }))}
      loading={!editor.loaded}
      maxLinks={maxStoreLinks}
      maxLabelLength={linkLabelMaxCharacters}
      onUpdate={({ index, patch }) =>
        editor.updateContent({
          links: links.map((link, i) =>
            i === index ? { ...link, ...patch } : link,
          ),
        })
      }
      onRemove={(index) =>
        editor.updateContent({ links: links.filter((_, i) => i !== index) })
      }
      onReorder={({ from, to }) => {
        const next = links.slice();
        const moved = next.splice(from, 1);
        next.splice(to, 0, ...moved);
        editor.updateContent({ links: next });
      }}
      onAdd={(link) => {
        if (!link.label) return "a label is required";

        if (link.label.length > linkLabelMaxCharacters)
          return `label: 1-${linkLabelMaxCharacters} characters`;

        if (!isAllowedLinkUrl(link.url)) return urlError;

        if (links.length >= maxStoreLinks)
          return `at most ${maxStoreLinks} links`;

        editor.updateContent({ links: [...links, link] });
        return null;
      }}
    />
  );
}
